import { el, clear, norm } from '../lib/dom.js';
import { maisParecidos } from '../lib/similaridade.js';

/**
 * Select PESQUISÁVEL com botão limpar. Modos:
 *  - opcoes: array {value,label}  -> filtro no cliente (listas pequenas/fixas).
 *  - buscar: async (termo)=>[{value,label}] -> busca no servidor (listas grandes).
 *  - permitirNovo: true -> combobox "criável" (só no modo opcoes): existentes sempre em foco e
 *    "cadastrar" é opção discreta no rodapé. Texto sem escolha que é TRECHO de um item existente
 *    conta como correspondência real -> vira 'ambiguo' (pede para escolher, evita duplicata); só
 *    vira NOVO quando NÃO corresponde a nada. O aviso "será cadastrado" só aparece após escolher criar.
 * getEscolha(): {tipo:'existente',value,label} | {tipo:'novo',texto} | {tipo:'ambiguo',texto} | null.
 * @returns {{elemento:HTMLElement, getValue:()=>any, getEscolha:()=>any, getTexto:()=>string}}
 */
export function buscaSelect({ opcoes = null, buscar = null, valor = null, label = '', aoEscolher = null, permitirNovo = false, criarRotulo = null, limite = 50 } = {}) {
  let escolha = (valor != null && label) ? { tipo: 'existente', value: valor, label } : null;
  let itens = [];
  let ativo = -1;
  let timer = null;
  const podeNovo = permitirNovo && !buscar; // "novo" só faz sentido em lista do cliente

  const input = el('input', { type: 'text', placeholder: podeNovo ? 'Digite para buscar ou cadastrar…' : 'Digite para buscar…', autocomplete: 'off' });
  const limpar = el('button', { type: 'button', class: 'input-x', title: 'Limpar', tabindex: '-1',
    onMousedown: (e) => { e.preventDefault(); escolha = null; input.value = ''; wrap.classList.remove('mov-picker--ok'); limpar.style.display = 'none'; atualizarHint(); if (aoEscolher) aoEscolher(null); input.focus(); atualizar(''); } },
    [el('i', { class: 'ph ph-x' })]);
  const lista = el('div', { class: 'mov-picker__lista' });
  const hint = podeNovo ? el('div', { class: 'mov-picker__novo', style: 'display:none' }) : null;
  const campoInput = el('div', { class: 'mov-picker__campo' }, [input, limpar]); // input+X juntos: o X ancora so no input, nao segue o dropdown
  const wrap = el('div', { class: 'mov-picker' }, [campoInput, lista, hint].filter(Boolean));

  if (escolha) { input.value = label; wrap.classList.add('mov-picker--ok'); }
  limpar.style.display = input.value ? '' : 'none';

  const rotuloBase = (o) => (o && o.nome != null ? o.nome : o.label); // nome limpo (sem sufixo "(aguardando aprovação)" de pendentes)
  const optExato = (tv) => { const t = norm(tv); return (opcoes || []).some((o) => norm(rotuloBase(o)) === t); };
  // Correspondencia REAL = o texto digitado e trecho (substring) de um item existente. NAO usa o fuzzy frouxo (0.45),
  // que serve so para SUGERIR no dropdown: usa-lo na decisao travava nome novo legitimo ("Sala 101" vs "Sala 202")
  // e fazia o Enter auto-selecionar o item errado.
  const temForte = (tv) => { const t = norm(tv); return !!t && (opcoes || []).some((o) => norm(rotuloBase(o)).includes(t)); };
  const fechar = () => { lista.style.display = 'none'; ativo = -1; };

  function escolherExistente(o) { escolha = { tipo: 'existente', value: o.value, label: o.label }; input.value = o.label; wrap.classList.add('mov-picker--ok'); limpar.style.display = ''; fechar(); atualizarHint(); if (aoEscolher) aoEscolher(o); }
  function escolherNovo(texto) { escolha = { tipo: 'novo', texto }; input.value = texto; wrap.classList.remove('mov-picker--ok'); limpar.style.display = ''; fechar(); atualizarHint(); if (aoEscolher) aoEscolher(null); }

  function atualizarHint() {
    if (!hint) return;
    // So avisa DEPOIS que o usuario escolheu deliberadamente "cadastrar novo" (nao enquanto digita).
    const criando = !!escolha && escolha.tipo === 'novo';
    hint.textContent = criando ? `“${escolha.texto}” será cadastrado quando você salvar.` : '';
    hint.style.display = criando ? '' : 'none';
  }

  function filtrar(termo) {
    const t = norm(termo);
    if (!t) return (opcoes || []).slice(0, limite);
    const subs = (opcoes || []).filter((o) => norm(rotuloBase(o)).includes(t));
    if (!podeNovo) return subs;
    const extra = maisParecidos(termo, opcoes || [], rotuloBase, 0.45).map((x) => x.it).filter((o) => !subs.includes(o));
    return [...subs, ...extra];
  }

  function marcar() { [...lista.querySelectorAll('.mov-picker__item')].forEach((n, k) => n.classList.toggle('mov-picker__item--ativo', k === ativo)); lista.querySelectorAll('.mov-picker__item')[ativo]?.scrollIntoView({ block: 'nearest' }); }

  function mostrar() {
    clear(lista);
    itens.slice(0, limite).forEach((o, i) => lista.append(el('div', {
      class: 'mov-picker__item', onMouseenter: () => { ativo = i; marcar(); }, onMousedown: (e) => { e.preventDefault(); escolherExistente(o); },
    }, o.label)));
    if (itens.length > limite) lista.append(el('div', { class: 'mov-picker__mais', style: 'padding:6px 10px;opacity:.6;font-size:.85em', onMousedown: (e) => e.preventDefault() }, `+${itens.length - limite} — digite para filtrar`));
    const tv = input.value.trim();
    const mostraNovo = podeNovo && tv && !optExato(tv);
    if (mostraNovo) {
      // "Criar" fica SEMPRE disponivel, mas discreto (rodape). Texto muda se ha ou nao correspondencias.
      const temItens = itens.length > 0;
      const rot = criarRotulo ? criarRotulo(tv, temItens)
        : (temItens ? `Nenhum destes? Cadastrar “${tv}” (vai para aprovação)` : `Cadastrar “${tv}” (vai para aprovação)`);
      lista.append(el('div', { class: 'mov-picker__item mov-picker__item--novo', onMousedown: (e) => { e.preventDefault(); escolherNovo(tv); } }, [el('i', { class: 'ph ph-plus' }), ' ', rot]));
    }
    lista.style.display = (itens.length || mostraNovo) ? 'block' : 'none';
    // So pre-seleciona o 1o item quando faz sentido: lista nao-criavel, busca vazia, ou ha correspondencia REAL.
    // Num combobox criavel com so itens "parecidos" (fuzzy), NAO auto-seleciona (senao Enter pegaria o item errado).
    ativo = (itens.length && (!podeNovo || temForte(tv))) ? 0 : -1; marcar();
  }

  async function atualizar(termo) {
    if (buscar) itens = await buscar(termo);
    else itens = filtrar(termo);
    mostrar();
  }

  input.addEventListener('input', () => {
    escolha = null; wrap.classList.remove('mov-picker--ok');
    limpar.style.display = input.value ? '' : 'none'; atualizarHint();
    clearTimeout(timer); timer = setTimeout(() => atualizar(input.value.trim()), buscar ? 250 : 0);
  });
  input.addEventListener('focus', () => { clearTimeout(timer); const t = (buscar && escolha && escolha.tipo === 'existente') ? '' : input.value.trim(); timer = setTimeout(() => atualizar(t), buscar ? 150 : 0); });
  input.addEventListener('blur', () => setTimeout(() => { fechar(); atualizarHint(); }, 150));
  input.addEventListener('keydown', (e) => {
    const aberta = lista.style.display === 'block' && itens.length;
    if (e.key === 'ArrowDown' && aberta) { e.preventDefault(); ativo = Math.min(ativo + 1, itens.length - 1); marcar(); }
    else if (e.key === 'ArrowUp' && aberta) { e.preventDefault(); ativo = Math.max(ativo - 1, 0); marcar(); }
    else if (e.key === 'Enter') {
      if (aberta && ativo >= 0) { e.preventDefault(); e.stopPropagation(); escolherExistente(itens[ativo]); }
      else if (podeNovo && input.value.trim() && !optExato(input.value.trim()) && !temForte(input.value.trim())) { e.preventDefault(); e.stopPropagation(); escolherNovo(input.value.trim()); }
    }
    else if (e.key === 'Escape' && lista.style.display === 'block') { e.preventDefault(); e.stopPropagation(); fechar(); }
  });

  function getEscolha() {
    if (escolha && escolha.tipo === 'existente') return escolha;
    const tv = input.value.trim();
    if (!tv) return null;
    const ex = (opcoes || []).find((o) => norm(rotuloBase(o)) === norm(tv));
    if (ex) return { tipo: 'existente', value: ex.value, label: ex.label };
    if (escolha && escolha.tipo === 'novo') return escolha;
    if (!podeNovo) return null;
    // texto livre sem escolha deliberada: se e TRECHO de um item existente (correspondencia real) -> AMBIGUO
    // (pede p/ escolher, evita duplicata); so vira NOVO quando nao corresponde a nada. Fuzzy nao conta aqui.
    return temForte(tv) ? { tipo: 'ambiguo', texto: tv } : { tipo: 'novo', texto: tv };
  }

  return {
    elemento: wrap,
    getValue: () => (escolha && escolha.tipo === 'existente') ? escolha.value : null,
    getEscolha,
    getTexto: () => input.value.trim(),
    escolher: (valor, label) => { // pre-seleciona uma opcao por value; se nao esta na lista (ex.: empresa inativa) e veio label, injeta p/ nao perder
      let o = (opcoes || []).find((x) => String(x.value) === String(valor));
      if (!o && valor != null && label) { o = { value: valor, label, nome: label }; if (opcoes) opcoes.push(o); }
      if (o) escolherExistente(o);
      return !!o;
    },
    limpar: () => { escolha = null; input.value = ''; wrap.classList.remove('mov-picker--ok'); limpar.style.display = 'none'; atualizarHint(); fechar(); },
  };
}